import LegalLayout from './LegalLayout';

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement between you and Veridex when using our platform."
      lastUpdated="19 April 2026"
    >
      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using the Veridex platform ("Service"), you agree to be bound by these
        Terms of Service. If you do not agree, do not use the Service.
      </p>

      <h2>2. Description of the Service</h2>
      <p>
        Veridex is a B2B software-as-a-service platform that helps organizations calculate,
        document and report product-level carbon footprints (PCF), prepare audit-ready
        documentation aligned with CBAM methodology, and generate ICS2 customs filings.
        Veridex is a <strong>decision-support and reporting-assistance</strong> platform —
        it does not replace your own obligations as an importer, declarant or reporter.
      </p>

      <h2>3. Accounts and Security</h2>
      <ul>
        <li>You must provide accurate, current information when creating an account.</li>
        <li>You are responsible for keeping your credentials, API keys and team access secure.</li>
        <li>You must notify us immediately of any unauthorized access at security@veridex-one.com.</li>
      </ul>

      <h2>4. Limitation of Liability</h2>
      <p>
        <strong>
          To the maximum extent permitted by applicable law, Veridex shall not be liable for any
          indirect, incidental, special, consequential or punitive damages, nor for any loss of
          profits, revenue, data, use, goodwill or other intangible losses, resulting from your
          use of — or inability to use — the Service.
        </strong>
      </p>
      <p>
        Our total aggregate liability arising out of or in connection with the Service shall
        not exceed the fees paid by you to Veridex in the twelve (12) months preceding the
        event giving rise to the claim.
      </p>

      <h2>5. No Legal Guarantee</h2>
      <p>
        The Service is provided <strong>"as is"</strong> and <strong>"as available"</strong>.
        Veridex makes no warranty — express or implied — that the Service:
      </p>
      <ul>
        <li>will meet any specific regulatory acceptance standard of any customs authority, tax authority or certification body;</li>
        <li>will be accepted by DAKOSY, PLDA, CLOSE, the European Commission or any national authority;</li>
        <li>will be uninterrupted, timely, secure or error-free.</li>
      </ul>
      <p>
        <strong>
          Veridex does not certify, approve or endorse your submissions. Acceptance of any
          CBAM report, ICS2 filing or carbon disclosure remains the sole decision of the
          competent authority.
        </strong>
      </p>

      <h2>6. Customer Responsibility for Final Submissions</h2>
      <p>
        You — the customer — are <strong>solely responsible</strong> for:
      </p>
      <ul>
        <li>the accuracy, completeness and lawfulness of all data you enter or upload;</li>
        <li>the final review, approval and submission of any CBAM report, customs declaration or regulatory filing;</li>
        <li>independently verifying any figure, calculation, emission factor or narrative produced by the Service before using it in a regulated context;</li>
        <li>retaining qualified tax, legal, customs and sustainability advisors where required.</li>
      </ul>
      <p>
        Nothing in the Service constitutes legal, tax, customs or compliance advice.
      </p>

      <h2>7. Artificial Intelligence Outputs</h2>
      <p>
        Parts of the Service use large-language-model ("AI") technology — notably CSV column
        mapping, invoice extraction, supplier estimation and the in-app assistant. AI outputs
        are <strong>indicative only, non-binding and may contain errors or hallucinations</strong>.
        You must apply human review before relying on any AI output for a regulated filing,
        financial decision or external communication.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        Veridex and its licensors own the Service, including software, design, brand and
        documentation. You retain ownership of the data you upload; you grant Veridex a
        limited licence to process that data for the sole purpose of delivering the Service
        to you.
      </p>

      <h2>9. Fees and Billing</h2>
      <p>
        Paid plans are billed through Stripe in accordance with the pricing shown at
        <a href="/pricing"> /pricing</a> at the time of purchase. Subscriptions renew
        automatically unless cancelled from the Stripe Customer Portal.
      </p>

      <h2>10. Suspension and Termination</h2>
      <p>
        You may terminate your account at any time from the Settings page. We may suspend
        or terminate access for breach of these Terms, non-payment, or suspected fraudulent
        or unlawful activity.
      </p>

      <h2>11. Governing Law and Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of the <strong>Federal Republic of Germany</strong>,
        without regard to conflict-of-law principles. Exclusive jurisdiction lies with the
        competent courts of Germany, except where mandatory consumer protection law provides
        otherwise.
      </p>

      <h2>12. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes will be announced by
        email and in the product. Continued use after an update constitutes acceptance.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms: <a href="mailto:legal@veridex-one.com">legal@veridex-one.com</a>.
      </p>
    </LegalLayout>
  );
}
